import { useState, useEffect } from "react";
import {
  Plus,
  Users,
  Calendar,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDealerPortal,
  JOURNEY_PAGE_SIZES,
  DEFAULT_JOURNEY_PAGE_SIZE,
} from "@/hooks/useDealerPortal";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";
import { useAuth } from "@/hooks/useAuth";
import {
  type DealerPortalCustomerJourney,
  type DealerPortalCustomerJourneyStatus,
  type DealerPortalJourneyPriority,
} from "@/types/dealerPortal";
import { LicensePlate } from "@/components/my-auctions/LicensePlate";
import { Badge } from "@/components/ui/badge";
import { DealerPortalNewFollowUpModal } from "./DealerPortalNewFollowUpModal";
import { DealerPortalJourneyCardModal } from "./DealerPortalJourneyCardModal";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { UserCheck } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { getLanguage } from "@/utils/languageUtils";
import {
  dealerPortalLabels,
  getJourneyStatusLabels,
  getJourneyPriorityLabels,
} from "@/i18n";

const getDateLocale = () => (getLanguage() === "en" ? "en-GB" : "sv-SE");

function formatDate(s: string | null) {
  if (!s) return "";
  const d = new Date(s);
  return d.toLocaleDateString(getDateLocale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateShort(s: string | null) {
  if (!s) return "";
  return new Date(s).toLocaleDateString(getDateLocale(), {
    day: "numeric",
    month: "short",
  });
}

const STATUS_ORDER: DealerPortalCustomerJourneyStatus[] = [
  "FIRST_CONTACT",
  "PRODUCT_VIEWING",
  "NEGOTIATION",
  "CLOSED",
  "NOT_COMPLETED",
];

const STATUS_COLORS: Record<DealerPortalCustomerJourneyStatus, string> = {
  FIRST_CONTACT: "from-blue-500 to-purple-600",
  PRODUCT_VIEWING: "from-purple-500 to-purple-700",
  NEGOTIATION: "from-pink-500 to-purple-600",
  CLOSED: "from-emerald-500 to-green-600",
  NOT_COMPLETED: "from-orange-500 to-red-600",
};

const STATUS_ICONS: Record<DealerPortalCustomerJourneyStatus, LucideIcon> = {
  FIRST_CONTACT: Users,
  PRODUCT_VIEWING: Calendar,
  NEGOTIATION: TrendingUp,
  CLOSED: CheckCircle,
  NOT_COMPLETED: AlertCircle,
};

const PRIORITY_BADGE: Record<string, string> = {
  HIGH: "bg-red-500/90 text-white",
  MEDIUM: "bg-amber-500/90 text-white",
  LOW: "bg-emerald-500/90 text-white",
};
const PRIORITY_BADGE_HIGHLIGHT: Record<string, string> = {
  HIGH: "data-[highlighted]:!bg-red-500/90",
  MEDIUM: "data-[highlighted]:!bg-amber-500/90",
  LOW: "data-[highlighted]:!bg-emerald-500/90",
};

export type DealerPortalKundresaTabProps = {
  initialOpenJourneyId?: string | null;
  onOpenJourneyId?: (id: string | null) => void;
  onClearOpenJourneyId?: () => void;
};

export function DealerPortalKundresaTab(props: DealerPortalKundresaTabProps) {
  const {
    initialOpenJourneyId = null,
    onOpenJourneyId,
    onClearOpenJourneyId,
  } = props;
  const [newFollowUpOpen, setNewFollowUpOpen] = useState(false);
  const [selectedJourneyId, setSelectedJourneyId] = useState<string | null>(
    initialOpenJourneyId ?? null,
  );
  const { language } = useLanguage();
  const statusLabels = getJourneyStatusLabels(language);
  const priorityLabels = getJourneyPriorityLabels(language);
  const journeyLabels = dealerPortalLabels(language).journey as Record<
    string,
    string
  >;
  const [statusFilter, setStatusFilter] =
    useState<DealerPortalCustomerJourneyStatus>("FIRST_CONTACT");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePersistedPageSize(
    "pageSize:dealerPortalJourneys",
    DEFAULT_JOURNEY_PAGE_SIZE,
    [...JOURNEY_PAGE_SIZES],
  );

  useEffect(() => {
    if (initialOpenJourneyId) setSelectedJourneyId(initialOpenJourneyId);
  }, [initialOpenJourneyId]);

  const {
    colleagues,
    journeyCounts,
    journeysByStatus,
    updateJourney: updateJourneyMutation,
    journeys,
    invalidate,
  } = useDealerPortal({
    journeyStatus: statusFilter,
    journeyPage: page,
    journeyPageSize: pageSize,
  });

  const { user: currentUser } = useAuth();
  const colleaguesData = colleagues.data ?? [];
  const myActorId = currentUser?.id
    ? colleaguesData.find(
        (c) => c.user?.id && String(c.user.id) === String(currentUser.id),
      )?.id
    : null;

  const counts = journeyCounts.data ?? {};
  const paginated = journeysByStatus.data;
  const isLoading = journeysByStatus.isLoading;
  const countForStatus = counts[statusFilter] ?? 0;

  const rawItems =
    paginated?.items ??
    (paginated as { data?: { items?: DealerPortalCustomerJourney[] } })?.data
      ?.items;
  const rawTotal =
    typeof paginated?.total === "number"
      ? paginated.total
      : (paginated as { data?: { total?: number } })?.data?.total;
  const hasPaginatedData = Array.isArray(rawItems) && rawItems.length > 0;

  const needFallback = countForStatus > 0 && !isLoading && !hasPaginatedData;
  const allJourneys = journeys.data ?? [];

  const displayedJourneys = hasPaginatedData
    ? rawItems
    : countForStatus > 0
      ? allJourneys.filter((j) => j.status === statusFilter)
      : [];
  const total =
    hasPaginatedData && typeof rawTotal === "number"
      ? rawTotal
      : displayedJourneys.length;
  const totalPages =
    pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const byStatus = STATUS_ORDER.map((status) => ({
    status,
    label: statusLabels[status],
    count: counts[status] ?? 0,
    color: STATUS_COLORS[status],
  }));

  const showPagination = total > pageSize;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base sm:text-lg font-semibold">
          {journeyLabels.board ?? "Kundresebrädan"}
        </h2>
        <Button
          variant="outline"
          size="sm"
          className="text-xs sm:text-sm"
          onClick={() => setNewFollowUpOpen(true)}
        >
          <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
          {journeyLabels.newJourney ?? "Ny kundresa"}
        </Button>
      </div>

      {/* Status cards row – click to filter */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
        {byStatus.map(({ status, label, count, color }) => {
          const Icon = STATUS_ICONS[status];
          return (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status)}
              className={`rounded-xl bg-gradient-to-br ${color} p-3 sm:p-4 text-white shadow-md text-left transition-all duration-200 hover:scale-[1.02] sm:hover:scale-[1.05] cursor-pointer ${
                statusFilter === status
                  ? "border-4 border-accent dark:border-white"
                  : "border-2 border-transparent"
              }`}
            >
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Icon className="h-4 w-4 sm:h-5 sm:w-5 opacity-90 shrink-0" />
                <span className="font-medium text-xs sm:text-sm leading-tight">
                  {label}
                </span>
              </div>
              <p className="text-xl sm:text-2xl font-bold mt-0.5 sm:mt-1">
                {count}
              </p>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">
          {journeyLabels.loading ?? "Laddar kundresor..."}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2.5 sm:gap-4">
          <div className="space-y-2">
            <h3 className="text-xs sm:text-sm font-medium text-muted-foreground">
              {statusLabels[statusFilter]} ({countForStatus})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {displayedJourneys.map((j) => (
                <JourneyCard
                  key={j.id}
                  journey={j}
                  isAssignedToMe={
                    !!(
                      j.assignedActorId &&
                      myActorId &&
                      j.assignedActorId === myActorId
                    )
                  }
                  priorityLabels={priorityLabels}
                  journeyLabels={journeyLabels}
                  onClick={() => {
                    setSelectedJourneyId(j.id);
                    onOpenJourneyId?.(j.id);
                  }}
                  onPriorityChange={(id, priority) => {
                    updateJourneyMutation.mutate(
                      { id, body: { priority } },
                      {
                        onSuccess: () => {
                          invalidate();
                          toast.success(
                            journeyLabels.toastPriorityUpdated ??
                              "Prioritet uppdaterad",
                          );
                        },
                      },
                    );
                  }}
                />
              ))}
            </div>
            {showPagination && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={total}
                itemsPerPage={pageSize}
                onPageChange={setPage}
                onItemsPerPageChange={(v) => {
                  setPageSize(v);
                  setPage(1);
                }}
                itemsPerPageOptions={[...JOURNEY_PAGE_SIZES]}
              />
            )}
          </div>
        </div>
      )}

      <DealerPortalNewFollowUpModal
        open={newFollowUpOpen}
        onClose={() => setNewFollowUpOpen(false)}
        onSuccess={() => {
          setNewFollowUpOpen(false);
          invalidate();
          toast.success(journeyLabels.toastCreated ?? "Kundresa skapad");
        }}
      />

      {selectedJourneyId && (
        <DealerPortalJourneyCardModal
          journeyId={selectedJourneyId}
          open={!!selectedJourneyId}
          onClose={() => setSelectedJourneyId(null)}
          onSuccess={() => {
            invalidate();
            setSelectedJourneyId(null);
            onClearOpenJourneyId?.();
          }}
        />
      )}
    </div>
  );
}

function JourneyCard({
  journey,
  isAssignedToMe,
  onClick,
  onPriorityChange,
  priorityLabels,
  journeyLabels,
}: {
  journey: DealerPortalCustomerJourney;
  isAssignedToMe: boolean;
  onClick: () => void;
  onPriorityChange: (
    journeyId: string,
    priority: DealerPortalJourneyPriority,
  ) => void;
  priorityLabels: Record<string, string>;
  journeyLabels: Record<string, string>;
}) {
  const priority = (journey.priority ||
    "MEDIUM") as DealerPortalJourneyPriority;
  const customerName =
    journey.customer?.name?.trim() ||
    journey.customer?.email ||
    (journeyLabels.unknownCustomer ?? "Okänd kund");
  const sortedComments = journey.comments?.length
    ? [...journey.comments].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
    : [];
  const latestComment = sortedComments[0] ?? null;

  return (
    <div
      role="button"
      tabIndex={0}
      className={`rounded-lg sm:rounded-xl border p-2.5 sm:p-4 shadow-sm hover:shadow-md transition-all cursor-pointer text-left w-full ${
        isAssignedToMe
          ? "border-primary/60 bg-primary/5 dark:bg-primary/10"
          : "border-border/80 bg-card"
      }`}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
    >
      {isAssignedToMe && (
        <div className="mb-1 sm:mb-1.5">
          <Badge
            variant="default"
            className="gap-0.5 text-[9px] sm:text-xs font-medium px-1.5 py-0 sm:px-2 sm:py-0.5"
          >
            <UserCheck className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
            {journeyLabels.assignedToMe ?? "Tilldelad till mig"}
          </Badge>
        </div>
      )}
      <div className="flex items-start justify-between gap-1.5 sm:gap-3 mb-1.5 sm:mb-3">
        <div onClick={(e) => e.stopPropagation()}>
          <Select
            value={priority}
            onValueChange={(v) =>
              onPriorityChange(journey.id, v as DealerPortalJourneyPriority)
            }
          >
            <SelectTrigger
              className={`h-6 sm:h-7 text-[10px] sm:text-xs font-medium rounded-full border-0 shadow-none focus:ring-2 focus:ring-offset-1 px-2 py-0.5 sm:px-3 sm:py-1 w-auto min-w-0 gap-0.5 sm:gap-1 cursor-pointer [&>svg]:h-3 [&>svg]:w-3 ${PRIORITY_BADGE[priority] ?? PRIORITY_BADGE.MEDIUM} text-white hover:brightness-95 [&>svg]:text-white [&>svg]:opacity-90`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(
                Object.keys(priorityLabels) as DealerPortalJourneyPriority[]
              ).map((p) => (
                <SelectItem
                  key={p}
                  value={p}
                  className={`${PRIORITY_BADGE[p] ?? PRIORITY_BADGE.MEDIUM} ${PRIORITY_BADGE_HIGHLIGHT[p] ?? PRIORITY_BADGE_HIGHLIGHT.MEDIUM} text-white rounded-sm border-0 cursor-pointer group focus:text-white data-[highlighted]:text-white`}
                >
                  <span className="inline-block transition-transform duration-200 group-data-[highlighted]:scale-[1.05]">
                    {priorityLabels[p]}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-0.5 sm:gap-1 shrink-0">
          <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          {journeyLabels.updated ?? "Uppdaterad"}{" "}
          {formatDateShort(journey.updatedAt)}
        </p>
      </div>

      <p className="font-semibold text-foreground text-sm sm:text-base">
        {customerName}
      </p>

      {journey.followUpNote && (
        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 line-clamp-2">
          {journey.followUpNote}
        </p>
      )}

      {latestComment && (
        <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 sm:mt-2 line-clamp-2 border-l-2 border-muted-foreground/30 pl-1.5 sm:pl-2">
          {latestComment.content}
        </p>
      )}

      {journey.vehicle && (
        <p className="text-[10px] sm:text-xs text-muted-foreground mt-1.5 sm:mt-2 flex items-center gap-1 sm:gap-1.5 flex-wrap">
          <LicensePlate
            lpn={journey.vehicle.regNr}
            className="text-[10px] sm:text-xs"
          />{" "}
          – {journey.vehicle.make} {journey.vehicle.model} (
          {journey.vehicle.year})
        </p>
      )}
    </div>
  );
}
